/**
 * AnnouncementsFeed Component
 * Extracted from HubDashboard.jsx
 * Handles: Company announcements/feed, likes, comments
 */

import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { MessageCircle, Plus, ThumbsUp, MessageSquare, RefreshCw, Trash2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AnnouncementsFeed({ isAdmin = false }) {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newAnn, setNewAnn] = useState({ title: '', content: '', priority: 'normal' });
  const [expandedComments, setExpandedComments] = useState({});
  const [comments, setComments] = useState({});
  const [newComment, setNewComment] = useState({});

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  const fetchAnnouncements = async () => {
    try {
      const res = await axios.get(`${API}/announcements`, { withCredentials: true });
      setAnnouncements(res.data);
    } catch (e) {
      console.error('Failed to load announcements');
    } finally {
      setLoading(false);
    }
  };

  const createAnnouncement = async () => {
    try {
      await axios.post(`${API}/announcements`, newAnn, { withCredentials: true });
      setNewAnn({ title: '', content: '', priority: 'normal' });
      setShowCreate(false);
      fetchAnnouncements();
      toast.success('Announcement posted!');
    } catch (e) {
      toast.error('Failed to post announcement');
    }
  };

  const deleteAnnouncement = async (annId) => {
    if (!window.confirm('Delete this announcement?')) return;
    try {
      await axios.delete(`${API}/announcements/${annId}`, { withCredentials: true });
      fetchAnnouncements();
      toast.success('Announcement deleted');
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const toggleLike = async (annId) => {
    try {
      const res = await axios.post(`${API}/announcements/${annId}/like`, {}, { withCredentials: true });
      setAnnouncements(prev => prev.map(a => 
        a.announcement_id === annId 
          ? { ...a, is_liked: res.data.action === 'liked', likes_count: res.data.likes_count }
          : a
      ));
    } catch (e) {
      console.error('Failed to like');
    }
  };

  const loadComments = async (annId) => {
    try {
      const res = await axios.get(`${API}/announcements/${annId}/comments`, { withCredentials: true });
      setComments(prev => ({ ...prev, [annId]: res.data }));
    } catch (e) {
      console.error('Failed to load comments');
    }
  };

  const addComment = async (annId) => {
    const content = newComment[annId];
    if (!content?.trim()) return;
    try {
      await axios.post(`${API}/announcements/${annId}/comments`, { content }, { withCredentials: true });
      setNewComment(prev => ({ ...prev, [annId]: '' }));
      loadComments(annId);
      fetchAnnouncements();
    } catch (e) {
      toast.error('Failed to add comment');
    }
  };

  const toggleComments = (annId) => {
    const isExpanded = expandedComments[annId];
    setExpandedComments(prev => ({ ...prev, [annId]: !isExpanded }));
    if (!isExpanded && !comments[annId]) {
      loadComments(annId);
    }
  };

  if (loading) return <div className="flex justify-center py-8"><RefreshCw className="w-6 h-6 animate-spin text-accent" /></div>;

  return (
    <div className="space-y-4 overflow-y-auto max-h-[600px]">
      <div className="flex justify-between items-center sticky top-0 bg-background/80 backdrop-blur-sm py-2 z-10">
        <h3 className="text-lg font-bold flex items-center gap-2">
          <MessageCircle className="w-5 h-5 text-accent" />
          Company Feed
        </h3>
        {isAdmin && (
          <Button size="sm" onClick={() => setShowCreate(true)}>
            <Plus className="w-4 h-4 mr-1" /> Post
          </Button>
        )}
      </div>

      {showCreate && (
        <div className="bg-card/80 rounded-2xl p-4 border border-accent/30">
          <input
            type="text"
            placeholder="Title"
            value={newAnn.title}
            onChange={(e) => setNewAnn({ ...newAnn, title: e.target.value })}
            className="w-full px-4 py-2 rounded-xl bg-secondary border border-border/30 mb-2"
          />
          <textarea
            placeholder="What's happening?"
            value={newAnn.content}
            onChange={(e) => setNewAnn({ ...newAnn, content: e.target.value })}
            className="w-full px-4 py-2 rounded-xl bg-secondary border border-border/30 resize-none"
            rows={3}
          />
          <div className="flex justify-between items-center mt-3">
            <select
              value={newAnn.priority}
              onChange={(e) => setNewAnn({ ...newAnn, priority: e.target.value })}
              className="px-3 py-1 rounded-lg bg-secondary border border-border/30"
            >
              <option value="normal">Normal</option>
              <option value="important">Important</option>
              <option value="urgent">Urgent</option>
            </select>
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button size="sm" onClick={createAnnouncement}>Post</Button>
            </div>
          </div>
        </div>
      )}

      {announcements.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p>No announcements yet</p>
        </div>
      ) : (
        announcements.map((ann) => (
          <div key={ann.announcement_id} className={`bg-card/60 rounded-2xl p-4 border ${
            ann.priority === 'urgent' ? 'border-red-500/50' :
            ann.priority === 'important' ? 'border-yellow-500/50' : 'border-border/30'
          }`}>
            <div className="flex justify-between items-start mb-2">
              <div>
                <h4 className="font-bold">{ann.title}</h4>
                <p className="text-xs text-muted-foreground">
                  {ann.author_name} • {new Date(ann.created_at).toLocaleDateString()}
                </p>
              </div>
              {ann.priority !== 'normal' && (
                <span className={`text-xs px-2 py-1 rounded-lg ${
                  ann.priority === 'urgent' ? 'bg-red-500/20 text-red-400' : 'bg-yellow-500/20 text-yellow-400'
                }`}>
                  {ann.priority}
                </span>
              )}
              {isAdmin && (
                <button onClick={() => deleteAnnouncement(ann.announcement_id)} className="p-1 hover:bg-red-500/20 rounded">
                  <Trash2 className="w-4 h-4 text-red-400" />
                </button>
              )}
            </div>
            <p className="text-sm mb-3 whitespace-pre-wrap">{ann.content}</p>
            
            <div className="flex items-center gap-4 pt-2 border-t border-border/20">
              <button 
                onClick={() => toggleLike(ann.announcement_id)}
                className={`flex items-center gap-1 text-sm ${ann.is_liked ? 'text-accent' : 'text-muted-foreground'} hover:text-accent`}
              >
                <ThumbsUp className={`w-4 h-4 ${ann.is_liked ? 'fill-current' : ''}`} />
                {ann.likes_count || 0}
              </button>
              <button 
                onClick={() => toggleComments(ann.announcement_id)}
                className="flex items-center gap-1 text-sm text-muted-foreground hover:text-accent"
              >
                <MessageSquare className="w-4 h-4" />
                {ann.comments_count || 0}
              </button>
            </div>

            {expandedComments[ann.announcement_id] && (
              <div className="mt-3 pt-3 border-t border-border/20 space-y-2">
                {(comments[ann.announcement_id] || []).map((comment, idx) => (
                  <div key={idx} className="bg-secondary/50 rounded-lg p-2">
                    <p className="text-sm">{comment.content}</p>
                    <p className="text-xs text-muted-foreground">{comment.author_name}</p>
                  </div>
                ))}
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Add a comment..."
                    value={newComment[ann.announcement_id] || ''}
                    onChange={(e) => setNewComment(prev => ({ ...prev, [ann.announcement_id]: e.target.value }))}
                    onKeyPress={(e) => e.key === 'Enter' && addComment(ann.announcement_id)}
                    className="flex-1 px-3 py-1.5 rounded-lg bg-secondary border border-border/30 text-sm"
                  />
                  <Button size="sm" onClick={() => addComment(ann.announcement_id)}>Post</Button>
                </div>
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
}
